// UB16 — cross-tab auth sync. Primary channel: BroadcastChannel API.
// Fallback for browsers without BC (older Safari, sandboxed iframes): the
// `storage` event on a sentinel localStorage key.

export type AuthBroadcastEvent = { type: 'signed-in'; userId: string } | { type: 'signed-out' };

const CHANNEL_NAME = 'ddm:auth';
const STORAGE_KEY = 'ddm:auth-broadcast';

type Listener = (event: AuthBroadcastEvent) => void;

type BroadcastHandle = {
  publish: (event: AuthBroadcastEvent) => void;
  subscribe: (listener: Listener) => () => void;
  close: () => void;
};

export function createAuthBroadcast(): BroadcastHandle {
  const listeners = new Set<Listener>();
  const hasBroadcastChannel = typeof BroadcastChannel !== 'undefined';
  const channel = hasBroadcastChannel ? new BroadcastChannel(CHANNEL_NAME) : null;

  const dispatch = (event: AuthBroadcastEvent) => {
    for (const listener of listeners) listener(event);
  };

  if (channel) {
    channel.addEventListener('message', (e) => {
      const event = normalize(e.data);
      if (event) dispatch(event);
    });
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue) as unknown;
      const event = normalize(parsed);
      if (event) dispatch(event);
    } catch {
      // ignore malformed payloads
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }

  return {
    publish(event) {
      if (channel) {
        channel.postMessage(event);
      }
      // Always also write to storage so non-BC tabs receive the event.
      if (typeof window !== 'undefined') {
        // Append a nonce so identical successive events still trigger storage.
        const payload = JSON.stringify({ ...event, _n: Date.now() });
        try {
          window.localStorage.setItem(STORAGE_KEY, payload);
        } catch {
          // localStorage may be unavailable (private mode, quota); ignore.
        }
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    close() {
      listeners.clear();
      channel?.close();
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    },
  };
}

function normalize(value: unknown): AuthBroadcastEvent | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { type?: unknown; userId?: unknown };
  if (v.type === 'signed-in' && typeof v.userId === 'string') {
    return { type: 'signed-in', userId: v.userId };
  }
  if (v.type === 'signed-out') return { type: 'signed-out' };
  return null;
}

let singleton: BroadcastHandle | null = null;

export function getAuthBroadcast(): BroadcastHandle {
  if (!singleton) singleton = createAuthBroadcast();
  return singleton;
}

export function resetAuthBroadcastForTests(): void {
  singleton?.close();
  singleton = null;
}
